<template>
  <v-content class="gb">
    <v-container fluid>
      <v-row v-if="is_account_view" align="center" justify="center">
        <v-col cols="12" md="12">
          <v-card :loading="status === 'loading'" class="fill-width table_theme card-theme custom-container">
            <v-row align="center" class="account-header">
              <v-col>
                <h2 class="medium-font billing-title">Account Info</h2>
              </v-col>
              <!-- Legacy Cancel Account button — only shown to FREE-mode
                   owners. Calculated-mode owners cancel from the Billing
                   section below (release-with-30-day-window flow). -->
              <v-col style="text-align: end;" v-if="user.user_role === 'owner' && !show_billing_section">
                <v-btn
                    rounded
                    depressed
                    class="px-6 btn-blanco btn-blanco--a btn-billing"
                    @click="show_CancelAccountModal"
                >Cancel Account
                </v-btn>
              </v-col>
            </v-row>
            <!-- Cancelled-tenant lockout banner shown only inside the
                 Account view for non-admin sub-users. The tenant admin
                 (owner / agent admin) sees the Reactivate card on
                 /owner/billing instead. -->
            <div v-if="locked_for_cancelled_tenant" class="account-cancel-notice">
              <strong>Your agent owner has cancelled this Kiey account.</strong>
              You can still export your data below. Email <a href="mailto:support@kiey.com">support@kiey.com</a> with questions.
            </div>
            <v-row class="account-fields">
              <v-col cols="6">
                <v-text-field v-model="company_name" label="Company Name" name="company_name"
                              id="company_name_billing" class="d-block" :disabled="locked_for_cancelled_tenant"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field
                    ref="addressField"
                    v-model="message_address"
                    :error-messages="addressError"
                    label="Company Address"
                    class="d-block"
                    placeholder="Please type your address..."
                    :disabled="locked_for_cancelled_tenant"
                ></v-text-field>
              </v-col>
            </v-row>
            <v-row class="account-fields" style="margin-top: -12px;">
              <v-col cols="6">
                <v-text-field v-model="phone_number" v-mask="mask" label="Username (Cell Phone Number)" id="phone_number_billing"
                              class="d-block" :disabled="locked_for_cancelled_tenant"></v-text-field>
              </v-col>
              <v-col cols="6">
                <v-text-field v-model="email" label="Email" id="email_billing" class="d-block" :disabled="locked_for_cancelled_tenant"></v-text-field>
              </v-col>
            </v-row>
            <div v-if="!locked_for_cancelled_tenant" class="text-center" style="margin: 20px 80px 30px;">
              <v-btn
                  rounded
                  depressed
                  class="btn-azul"
                  :loading="status === 'loading'"
                  @click="saveChangesInfo"
              >SAVE
              </v-btn>
            </div>

            <div class="privacy-section">
              <div class="privacy-row">
                <div class="privacy-text">
                  <h3 class="privacy-title">Data Export</h3>
                  <p class="privacy-desc">Download a ZIP archive of everything Kiey stores about your account — profile, messages, files, contacts, calls, voicemails, and invoices. We'll email you a download link when it's ready (usually within a few minutes). The link expires after 7 days.</p>
                </div>
                <button type="button" class="privacy-btn" :disabled="exporting" @click="onExportClick">
                  {{ exporting ? 'Preparing export…' : (exportRequested ? 'Export requested' : 'Export My Data') }}
                </button>
              </div>
              <p v-if="exportError" class="privacy-error">{{ exportError }}</p>
              <p v-if="exportRequested && !exportError" class="privacy-success">We're preparing your export. Check {{ user.email }} in a few minutes.</p>
            </div>

            <div class="legal-section">
              <h3 class="legal-title">Legal Acceptance Records</h3>
              <p class="legal-intro">
                Your audit trail of every contractual acceptance on this account.
                These records are kept indefinitely for legal compliance.
              </p>

              <!-- Terms of Service -->
              <div class="legal-record-card" v-if="user.tos_accepted_version">
                <div class="legal-record-head">
                  <span class="legal-record-title">Terms of Service</span>
                  <span class="legal-record-badge">✓ Accepted</span>
                </div>
                <dl class="legal-record-fields">
                  <dt>Version</dt>
                  <dd><code>{{ user.tos_accepted_version }}</code></dd>
                  <dt>Accepted</dt>
                  <dd>{{ formatted_tos_accepted_at || '—' }}</dd>
                  <dt v-if="user.tos_accepted_ip">IP address</dt>
                  <dd v-if="user.tos_accepted_ip"><code>{{ user.tos_accepted_ip }}</code></dd>
                </dl>
              </div>
              <div class="legal-record-card legal-record-card--missing" v-else>
                <div class="legal-record-head">
                  <span class="legal-record-title">Terms of Service</span>
                  <span class="legal-record-badge legal-record-badge--missing">Not on file</span>
                </div>
                <p class="legal-record-empty">No Terms of Service acceptance is recorded for this account.</p>
              </div>

              <!-- Billing Terms — only present for paying (calculated/custom) tenants who completed checkout -->
              <div class="legal-record-card" v-if="user.billing_terms_accepted_version">
                <div class="legal-record-head">
                  <span class="legal-record-title">Billing Terms</span>
                  <span class="legal-record-badge">✓ Accepted</span>
                </div>
                <dl class="legal-record-fields">
                  <dt>Version</dt>
                  <dd><code>{{ user.billing_terms_accepted_version }}</code></dd>
                  <dt>Accepted</dt>
                  <dd>{{ formatted_billing_terms_accepted_at || '—' }}</dd>
                  <dt v-if="user.billing_terms_accepted_ip">IP address</dt>
                  <dd v-if="user.billing_terms_accepted_ip"><code>{{ user.billing_terms_accepted_ip }}</code></dd>
                </dl>
              </div>

              <!-- Free Tier Partner Agreement — only present for free-tier tenants who accepted the partner agreement -->
              <div class="legal-record-card" v-if="user.free_terms_accepted_version">
                <div class="legal-record-head">
                  <span class="legal-record-title">Free Tier Partner Agreement</span>
                  <span class="legal-record-badge">✓ Accepted</span>
                </div>
                <dl class="legal-record-fields">
                  <dt>Version</dt>
                  <dd><code>{{ user.free_terms_accepted_version }}</code></dd>
                  <dt>Accepted</dt>
                  <dd>{{ formatted_free_terms_accepted_at || '—' }}</dd>
                  <dt v-if="user.free_terms_accepted_ip">IP address</dt>
                  <dd v-if="user.free_terms_accepted_ip"><code>{{ user.free_terms_accepted_ip }}</code></dd>
                </dl>
              </div>

              <p class="legal-links">
                <a href="/terms_and_conditions" target="_blank" rel="noopener">View current Terms of Service</a>
                &middot;
                <a href="/privacy_policy" target="_blank" rel="noopener">Privacy Policy</a>
                &middot;
                <a href="/dpa" target="_blank" rel="noopener">Data Processing Addendum</a>
              </p>
            </div>

          </v-card>
        </v-col>
      </v-row>
      <!-- Free Tier Billing view. Calculated-mode tenants fall through to
           the Stripe-managed Billing section below. This section renders
           for FREE-mode tenants visiting /owner/billing so they see a
           real page (plan summary + their Free Tier Partner Agreement
           acceptance) instead of a blank tab. -->
      <v-row v-if="is_billing_view && is_free_tier" align="center" justify="center">
        <v-col cols="12" md="12">
          <div class="billing-section">
            <div class="billing-section-header">
              <h2 class="billing-section-title">Billing</h2>
            </div>
            <div class="plan-card free-tier-card">
              <div class="plan-card-row">
                <div class="plan-card-cell">
                  <div class="plan-card-label">Current plan</div>
                  <div class="plan-card-value">Kiey Free Tier</div>
                </div>
                <div class="plan-card-cell">
                  <div class="plan-card-label">Monthly</div>
                  <div class="plan-card-value">$0</div>
                </div>
                <div class="plan-card-cell">
                  <div class="plan-card-label">Status</div>
                  <div class="plan-card-value">
                    <span class="status-badge status-badge-active">Active</span>
                  </div>
                </div>
              </div>
              <p class="free-tier-blurb">
                You're on the Kiey Free Tier — no subscription, no monthly charge. You retain access under the terms of the Free Tier Partner Agreement (recorded below). If you'd like to switch to a paid market with ZIP exclusivity, contact <a href="mailto:support@kiey.com">support@kiey.com</a>.
              </p>
            </div>

            <!-- Legal acceptance records, mirrored from the Account view so
                 free-tier owners see their Free Tier Partner Agreement
                 + Terms of Service receipts on the Billing tab too. -->
            <div class="legal-section">
              <h3 class="legal-title">Legal Acceptance Records</h3>
              <p class="legal-intro">
                Your audit trail of every contractual acceptance on this account.
                These records are kept indefinitely for legal compliance.
              </p>

              <div class="legal-record-card" v-if="user.tos_accepted_version">
                <div class="legal-record-head">
                  <span class="legal-record-title">Terms of Service</span>
                  <span class="legal-record-badge">✓ Accepted</span>
                </div>
                <dl class="legal-record-fields">
                  <dt>Version</dt>
                  <dd><code>{{ user.tos_accepted_version }}</code></dd>
                  <dt>Accepted</dt>
                  <dd>{{ formatted_tos_accepted_at || '—' }}</dd>
                  <dt v-if="user.tos_accepted_ip">IP address</dt>
                  <dd v-if="user.tos_accepted_ip"><code>{{ user.tos_accepted_ip }}</code></dd>
                </dl>
              </div>

              <div class="legal-record-card" v-if="user.free_terms_accepted_version">
                <div class="legal-record-head">
                  <span class="legal-record-title">Free Tier Partner Agreement</span>
                  <span class="legal-record-badge">✓ Accepted</span>
                </div>
                <dl class="legal-record-fields">
                  <dt>Version</dt>
                  <dd><code>{{ user.free_terms_accepted_version }}</code></dd>
                  <dt>Accepted</dt>
                  <dd>{{ formatted_free_terms_accepted_at || '—' }}</dd>
                  <dt v-if="user.free_terms_accepted_ip">IP address</dt>
                  <dd v-if="user.free_terms_accepted_ip"><code>{{ user.free_terms_accepted_ip }}</code></dd>
                </dl>
              </div>

              <p class="legal-links">
                <a href="/terms_and_conditions" target="_blank" rel="noopener">View current Terms of Service</a>
                &middot;
                <a href="/privacy_policy" target="_blank" rel="noopener">Privacy Policy</a>
                &middot;
                <a href="/dpa" target="_blank" rel="noopener">Data Processing Addendum</a>
              </p>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Subscription billing — only rendered for tenants on calculated
           pricing (Stripe-managed). FREE / custom-priced tenants don't see
           this section at all; they keep the Account Info form + the
           legacy Cancel Account button above. -->
      <v-row v-if="show_billing_section && is_billing_view" align="center" justify="center">
        <v-col cols="12" md="12">
          <div class="billing-section">
            <div class="billing-section-header">
              <h2 class="billing-section-title">Billing</h2>
              <button
                v-if="billing_info && !billing_info_loading && !billing_info_error"
                type="button"
                class="manage-billing-btn billing-section-action"
                :disabled="opening_portal"
                @click="openBillingPortal"
              >
                {{ opening_portal ? 'Opening&hellip;' : 'Manage Billing' }}
              </button>
            </div>
            <p v-if="user.user_role !== 'owner'" class="billing-section-subtitle">
              Subscription details and invoices for this market.
            </p>

            <div v-if="billing_info_loading" class="billing-loading">
              Loading billing details&hellip;
            </div>
            <div v-else-if="billing_info_error" class="billing-error">
              {{ billing_info_error }}
            </div>
            <!-- Cancelled-state card. Renders when the tenant's
                 subscription has been deleted by Stripe (post 30-day cancel
                 fire OR immediate Stripe-side cancel). Replaces the normal
                 billing card so the user sees a clear next-step instead
                 of an empty / broken Active card. -->
            <div v-else-if="billing_info && is_cancelled_state" class="cancelled-card">
              <div class="cancelled-card-row">
                <div class="cancelled-card-icon">⊘</div>
                <div class="cancelled-card-body">
                  <h3 class="cancelled-card-title">Your subscription has ended</h3>
                  <p class="cancelled-card-meta" v-if="formatted_canceled_at">
                    Cancelled on {{ formatted_canceled_at }}
                  </p>
                  <div v-if="archived_market" class="cancelled-card-detail">
                    <strong>Last market:</strong>
                    {{ archived_market.zip_count }} ZIP{{ archived_market.zip_count === 1 ? '' : 's' }}<span v-if="archived_market.population"> · ~{{ Number(archived_market.population).toLocaleString('en-US') }} residents</span><span v-if="archived_market.monthly"> · ${{ Number(archived_market.monthly).toLocaleString('en-US') }}/month</span>
                  </div>
                  <p class="cancelled-card-help">
                    Reactivate to restart billing and reclaim your market. ZIPs that another agent has since claimed will be flagged so you can pick replacements.
                    <strong v-if="cancelled_last_monthly">You will be charged ${{ Number(cancelled_last_monthly).toLocaleString('en-US') }}/month on the same Stripe customer.</strong>
                  </p>
                </div>
                <button
                  type="button"
                  class="reactivate-btn"
                  :disabled="reactivate_open"
                  @click="onOpenReactivate"
                >
                  Reactivate Subscription
                </button>
              </div>

              <!-- Inline reactivate flow: lazy-loads Stripe Elements only
                   when the user clicks Reactivate, then collects card +
                   POSTs to /v1/markets/reactivate. -->
              <div v-if="reactivate_open" class="reactivate-flow">
                <p v-if="reactivate_load_error" class="reactivate-error">{{ reactivate_load_error }}</p>
                <p v-else-if="!reactivate_elements_ready" class="reactivate-loading">Loading secure payment form…</p>
                <div ref="reactivate_payment_element" class="reactivate-element-mount"></div>
                <p v-if="reactivate_error" class="reactivate-error">{{ reactivate_error }}</p>
                <div v-if="reactivate_unavailable_zips && reactivate_unavailable_zips.length" class="reactivate-warning">
                  Heads up — {{ reactivate_unavailable_zips.length }} of your old ZIPs have been claimed by another agent and won't be reclaimed. You can pick replacements after reactivating.
                </div>
                <p v-if="cancelled_last_monthly" class="reactivate-price-line">
                  Today's charge: <strong>${{ Number(cancelled_last_monthly).toLocaleString('en-US') }}.00</strong> (recurring monthly). Tax computed at checkout based on your billing address.
                </p>
                <div class="reactivate-actions">
                  <button
                    type="button"
                    class="reactivate-cancel"
                    :disabled="reactivate_submitting"
                    @click="closeReactivate"
                  >Cancel</button>
                  <button
                    type="button"
                    class="reactivate-submit"
                    :disabled="!reactivate_elements_ready || reactivate_submitting"
                    @click="onSubmitReactivate"
                  >
                    {{ reactivate_submitting ? 'Reactivating…' : (cancelled_last_monthly ? `Reactivate Now — $${Number(cancelled_last_monthly).toLocaleString('en-US')}.00/mo` : 'Reactivate Now') }}
                  </button>
                </div>
              </div>

              <!-- Cancelled tenants still need visibility into what they
                   were paying + their full invoice history (kept by
                   Stripe). These render below the Reactivate card so the
                   user has a complete record of their subscription. -->
              <div v-if="cancelled_last_monthly" class="plan-card cancelled-history-card">
                <div class="plan-card-row">
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Previous plan</div>
                    <div class="plan-card-value">${{ Number(cancelled_last_monthly).toLocaleString('en-US') }}/month</div>
                  </div>
                  <div class="plan-card-cell" v-if="archived_market && archived_market.zip_count">
                    <div class="plan-card-label">Market size</div>
                    <div class="plan-card-value">{{ archived_market.zip_count }} ZIP{{ archived_market.zip_count === 1 ? '' : 's' }}<span v-if="archived_market.population"> · ~{{ Number(archived_market.population).toLocaleString('en-US') }} pop</span></div>
                  </div>
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Status</div>
                    <div class="plan-card-value">
                      <span class="status-badge status-badge-canceled">Cancelled</span>
                    </div>
                  </div>
                  <div class="plan-card-cell" v-if="billing_info.payment_method_last4">
                    <div class="plan-card-label">Last payment method</div>
                    <div class="plan-card-value">
                      &bull;&bull;&bull;&bull; {{ billing_info.payment_method_last4 }}
                    </div>
                  </div>
                </div>
              </div>

              <h3 class="invoices-title">Invoices</h3>
              <div v-if="!billing_info.invoices || billing_info.invoices.length === 0" class="invoices-empty">
                No invoices on file.
              </div>
              <table v-else class="invoices-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in billing_info.invoices" :key="invoice.id">
                    <td>{{ format_invoice_date(invoice.paid_at) }}</td>
                    <td>{{ format_invoice_amount(invoice.amount) }}</td>
                    <td>
                      <span class="invoice-status" :class="invoice_status_class(invoice.status)">
                        {{ format_invoice_status(invoice.status) }}
                      </span>
                    </td>
                    <td>
                      <a v-if="invoice.hosted_invoice_url" :href="invoice.hosted_invoice_url" target="_blank" rel="noopener noreferrer" class="invoice-link">View</a>
                      <a v-if="invoice.invoice_pdf" :href="invoice.invoice_pdf" target="_blank" rel="noopener noreferrer" class="invoice-link invoice-link-download">Download</a>
                      <span v-if="!invoice.hosted_invoice_url && !invoice.invoice_pdf">&mdash;</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else-if="billing_info">
              <!-- Persistent unavailable-ZIPs warning, surfaced after a
                   successful Reactivate when some old ZIPs were taken by
                   another agent in the meantime. Stays visible after the
                   reactivate-flow div collapses so the user actually sees it. -->
              <div
                v-if="reactivate_unavailable_zips && reactivate_unavailable_zips.length"
                class="reactivate-warning reactivate-warning--persistent"
              >
                <strong>Subscription reactivated</strong> — but {{ reactivate_unavailable_zips.length }} of your old ZIPs ({{ reactivate_unavailable_zips.slice(0,5).join(', ') }}{{ reactivate_unavailable_zips.length > 5 ? '…' : '' }}) have been claimed by another agent and weren't reclaimed. Visit Account → Market Region to pick replacements.
                <button type="button" class="reactivate-warning-dismiss" @click="reactivate_unavailable_zips = []">Dismiss</button>
              </div>
              <!-- Current plan card -->
              <div class="plan-card">
                <div class="plan-card-row">
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Current plan</div>
                    <div class="plan-card-value">
                      <span v-if="billing_info.current_price">${{ formatted_price }}/month</span>
                      <span v-else>&mdash;</span>
                    </div>
                  </div>
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Status</div>
                    <div class="plan-card-value">
                      <span class="status-badge" :class="status_badge_class">
                        {{ status_label }}
                      </span>
                    </div>
                  </div>
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Renewal date</div>
                    <div class="plan-card-value">{{ formatted_next_renewal }}</div>
                  </div>
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Next payment</div>
                    <div class="plan-card-value">{{ next_payment_label }}</div>
                    <div v-if="coupon_note" class="plan-card-subnote">{{ coupon_note }}</div>
                  </div>
                  <div class="plan-card-cell">
                    <div class="plan-card-label">Payment method</div>
                    <div class="plan-card-value">
                      <span v-if="billing_info.payment_method_last4">
                        &bull;&bull;&bull;&bull; {{ billing_info.payment_method_last4 }}
                      </span>
                      <span v-else>&mdash;</span>
                    </div>
                  </div>
                </div>

              </div>

              <!-- Invoices table -->
              <h3 class="invoices-title">Invoices</h3>
              <div v-if="!billing_info.invoices || billing_info.invoices.length === 0" class="invoices-empty">
                No invoices yet.
              </div>
              <table v-else class="invoices-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="invoice in billing_info.invoices" :key="invoice.id">
                    <td>{{ format_invoice_date(invoice.paid_at) }}</td>
                    <td>{{ format_invoice_amount(invoice.amount) }}</td>
                    <td>
                      <span class="invoice-status" :class="invoice_status_class(invoice.status)">
                        {{ format_invoice_status(invoice.status) }}
                      </span>
                    </td>
                    <td>
                      <a
                        v-if="invoice.hosted_invoice_url"
                        :href="invoice.hosted_invoice_url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="invoice-link"
                      >View</a>
                      <a
                        v-if="invoice.invoice_pdf"
                        :href="invoice.invoice_pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="invoice-link invoice-link-download"
                      >Download</a>
                      <span v-if="!invoice.hosted_invoice_url && !invoice.invoice_pdf">&mdash;</span>
                    </td>
                  </tr>
                </tbody>
              </table>

              <!-- Cancel Subscription / Resume Subscription -->
              <div class="cancel-link-row">
                <button
                  v-if="is_cancel_scheduled"
                  type="button"
                  class="resume-subscription-link"
                  :disabled="resuming"
                  @click="resumeSubscription"
                >
                  {{ resuming ? 'Resuming…' : 'Resume Subscription' }}
                </button>
                <button
                  v-else
                  type="button"
                  class="cancel-subscription-link"
                  @click="show_CancelAccountModal"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>

      <update-modal :show="showUpdateModal" :hide="hideUpdateModal"/>
      <cancel-modal
        :show="showCancelAccountModal"
        :hide="hideCancelAccountModal"
        :pricing-mode="active_pricing_mode"
      />
      <unsaved-modal :show="showUnsavedModal" :hide="hideUnsavedModal" :onSave="saveAndNavigate"/>
    </v-container>
  </v-content>
</template>

<script>
  import updateModal from "./modals/update";
  import cancelAccountModal from "./modals/cancel";
  import unsavedModal from "./modals/unsaved";
  import axios from "axios";
  import config from "@/config/constants.js";
  import {mapState, mapGetters} from "vuex";
  import {mask} from "vue-the-mask";
  import {extractLocation} from "@/util/places";
  import {requestDataExport} from "@/util/data_export";

  export default {
    props: {
      deleUser: {
        default: false
      },
      defaultValue: {
        type: Object,
        default: null
      },
      countrySelected: {
        type: String,
        default: null
      }
    },
    computed: {
      ...mapState({
        status: state => state.Billing.status,
        user: state => state.Authenticate.user,
        my_market: state => state.Market_billing.my_market,
        billing_info: state => state.Market_billing.billing_info,
      }),
      ...mapGetters('Authenticate', {
        tenant_billing_state: 'tenant_billing_state',
        is_tenant_admin: 'is_tenant_admin',
      }),
      // Non-admin sub-users in a cancelled tenant get a read-only Account
      // view (form disabled, no Save button) + a notice + Data Export.
      // Tenant admins are routed to /owner/billing instead, so this only
      // fires for the sub-user path.
      locked_for_cancelled_tenant() {
        return this.tenant_billing_state === 'cancelled' && !this.is_tenant_admin;
      },
      // /owner/account = Account Info + Data Export + Legal sections.
      // /owner/billing = Billing card + Invoices + Cancel link.
      // Default to account view when route name is neither (defensive).
      is_account_view() {
        return this.$route.name !== 'owner.billing';
      },
      is_billing_view() {
        return this.$route.name === 'owner.billing';
      },
      // Pricing mode for the currently-logged-in tenant. Sourced from
      // my_market.pricing_mode (populated by Market_billing/fetch_markets);
      // falls back to 'calculated' so we don't accidentally hide billing
      // from a tenant whose my_market hasn't loaded yet.
      active_pricing_mode() {
        if (this.my_market && this.my_market.pricing_mode) {
          return this.my_market.pricing_mode;
        }
        return 'calculated';
      },
      // Gate the new Billing section. Only calculated-mode tenants have a
      // Stripe subscription to manage; FREE tenants see only Account Info.
      show_billing_section() {
        return this.active_pricing_mode === 'calculated';
      },
      // FREE-mode tenants — used to render the Free Tier billing view on
      // /owner/billing so the tab isn't blank for them.
      is_free_tier() {
        return this.active_pricing_mode === 'free';
      },
      formatted_price() {
        const cents = this.billing_info && this.billing_info.current_price;
        if (typeof cents !== 'number' || cents <= 0) return '0';
        return (cents / 100).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
      },
      formatted_next_renewal() {
        const ts = this.billing_info && this.billing_info.next_renewal_at;
        return this.format_timestamp(ts);
      },
      is_cancel_scheduled() {
        return !!(this.billing_info && this.billing_info.cancel_at);
      },
      // True once Stripe has actually fired customer.subscription.deleted
      // (the 30-day cancel landed). Webhook flips billing_active=false +
      // billing_status='canceled' + clears the sub id at that point.
      is_cancelled_state() {
        if (!this.billing_info) return false;
        const status = this.billing_info.status || this.billing_info.subscription_status;
        return this.billing_info.billing_active === false
          || status === 'canceled'
          || status === 'cancelled';
      },
      formatted_canceled_at() {
        const ts = this.billing_info && this.billing_info.canceled_at;
        if (!ts) return '';
        return this.format_timestamp(ts);
      },
      // Last monthly amount the tenant was paying before cancel. Used by
      // the cancelled-state "Previous plan" card so the user can see
      // what they were on. Prefers the archived snapshot but falls back
      // to monthly_price_calculated for tenants whose archive wasn't
      // populated (pre-archive cancellations).
      cancelled_last_monthly() {
        if (!this.billing_info) return null;
        return this.billing_info.monthly_price_calculated_archive
          || this.billing_info.monthly_price_calculated
          || null;
      },
      // Snapshot of the market the tenant had at cancel time, populated
      // by stripe_webhook into market_region_archive. Used by the cancelled
      // card to show "what they're reactivating".
      archived_market() {
        if (!this.billing_info) return null;
        const region = this.billing_info.market_region_archive
          || (this.billing_info.subscription && this.billing_info.subscription.market_region_archive)
          || null;
        if (!region) return null;
        const zips = Array.isArray(region.zip_codes) ? region.zip_codes : [];
        return {
          zip_count: zips.length,
          population: this.billing_info.market_population_archive || region.population || null,
          monthly: this.billing_info.monthly_price_calculated_archive || null,
        };
      },
      formatted_cancel_at() {
        const ts = this.billing_info && this.billing_info.cancel_at;
        return this.format_timestamp(ts);
      },
      status_label() {
        if (this.is_cancel_scheduled) {
          return `Cancelling ${this.formatted_cancel_at}`;
        }
        const raw = (this.billing_info && this.billing_info.subscription_status) || 'unknown';
        return raw.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      },
      status_badge_class() {
        if (this.is_cancel_scheduled) return 'status-badge-warning';
        const raw = (this.billing_info && this.billing_info.subscription_status) || '';
        if (raw === 'active' || raw === 'trialing') return 'status-badge-active';
        if (raw === 'past_due' || raw === 'unpaid') return 'status-badge-warning';
        if (raw === 'canceled' || raw === 'cancelled' || raw === 'incomplete_expired') return 'status-badge-canceled';
        return 'status-badge-neutral';
      },
      next_payment_label() {
        const cents = this.billing_info && this.billing_info.next_payment_amount;
        if (typeof cents !== 'number') return '—';
        const dollars = (cents / 100).toLocaleString('en-US', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        });
        return `$${dollars} + tax`;
      },
      coupon_note() {
        const c = this.billing_info && this.billing_info.coupon;
        if (!c) return '';
        if (typeof c.percent_off === 'number' && c.percent_off > 0) {
          const pct = c.percent_off % 1 === 0 ? c.percent_off : c.percent_off.toFixed(1);
          if (c.duration === 'forever') return `${pct}% off (forever)`;
          if (c.duration === 'repeating' && c.duration_in_months) {
            return `${pct}% off (${c.duration_in_months} mo)`;
          }
          return `${pct}% off`;
        }
        if (typeof c.amount_off === 'number' && c.amount_off > 0) {
          return `$${(c.amount_off / 100).toFixed(2)} off`;
        }
        return '';
      },
      company_name: {
        get: function () {
          let company_name_current = '';
          for (const tenant of this.user.tenants_list) {
            if (this.user.tenant_current_logged == tenant.tenant_id) {
              company_name_current = tenant.company_name
            }
          }
          return company_name_current;
        },
        set: function (newValue) {
          return newValue;
        }
      },
      address: {
        get: function () {
          return this.user.address
        },
        set: function (newValue) {

          return newValue;
        }
      },
      phone_number: {
        get: function () {
          let number = this.user.phone_number.substring(2);

          return number;
        },
        set: function (newValue) {
          return newValue;
        }
      },
      email: {
        get: function () {
          return this.user.email
        },
        set: function (newValue) {
          return newValue;
        }
      },
      // Acceptance timestamps come from Firestore as one of:
      //   - a millisecond number (Date.now()),
      //   - an ISO string,
      //   - a Firestore-encoded {_seconds, _nanoseconds} object.
      // Normalize and format with date + local time so the audit
      // trail is human-readable on the account page.
      formatted_tos_accepted_at() {
        return this.formatAcceptanceTimestamp(this.user && this.user.tos_accepted_at);
      },
      formatted_billing_terms_accepted_at() {
        return this.formatAcceptanceTimestamp(this.user && this.user.billing_terms_accepted_at);
      },
      formatted_free_terms_accepted_at() {
        return this.formatAcceptanceTimestamp(this.user && this.user.free_terms_accepted_at);
      }
    },
    created() {
      this.message_address = this.user.address;
      this.originalValues = {
        company_name: this.company_name,
        message_address: this.user.address || '',
        phone_number: this.phone_number,
        email: this.user.email || '',
      };
    },
    mounted() {
      this.initAutocomplete();
      // Pull market data so we know which pricing_mode this tenant is on
      // before deciding whether to show the Billing section. fetch_markets
      // is cached for 60s so re-mounting is cheap.
      this.$store.dispatch('Market_billing/fetch_markets').finally(() => {
        if (this.show_billing_section) {
          this.loadBillingInfo();
        }
      });
    },
    beforeRouteLeave(to, from, next) {
      if (this.allowLeave) {
        next();
        return;
      }
      if (this.hasUnsavedChanges()) {
        this.pendingNext = next;
        this.showUnsavedModal = true;
      } else {
        next();
      }
    },
    directives: {
      mask,
    },
    data() {
      return {
        showUpdateModal: false,
        showCancelAccountModal: false,
        showUnsavedModal: false,
        pendingNext: null,
        allowLeave: false,
        originalValues: {},
        mask: "### - ### - ####",
        message_address: null,
        location: null,
        addressError: '',
        exporting: false,
        exportRequested: false,
        exportError: '',
        billing_info_loading: false,
        billing_info_error: '',
        opening_portal: false,
        resuming: false,
        // Inline Reactivate flow on the cancelled-state card.
        reactivate_open: false,
        reactivate_submitting: false,
        reactivate_elements_ready: false,
        reactivate_load_error: '',
        reactivate_error: '',
        reactivate_unavailable_zips: [],
        reactivate_stripe: null,
        reactivate_elements: null,
        reactivate_payment_element: null,
        reactivate_setup_intent_id: null,
      };
    },
    watch: {
      message_address(newVal) {
        this.addressError = '';
        // Stored coords are only valid for the exact address we picked;
        // any manual edit invalidates them and re-requires a dropdown pick.
        if (this.location && this.location.address !== newVal) {
          this.location = null;
        }
      },
    },
    components: {
      "update-modal": updateModal,
      "cancel-modal": cancelAccountModal,
      "unsaved-modal": unsavedModal,
    },
    methods: {
      // Normalize an acceptance timestamp from any of the shapes we see
      // on a users/{uid} doc — Firestore Timestamp ({ seconds, nanoseconds }
      // when serialized via REST, or a Timestamp instance with toDate()),
      // ISO date string, JS Date, or millisecond number — into a friendly
      // "Mon DD, YYYY · h:mm a" string for the audit trail. Returns ''
      // when the input is falsy or unparseable so the template falls
      // through to the '—' placeholder instead of throwing in render.
      formatAcceptanceTimestamp(ts) {
        if (!ts) return '';
        let d;
        try {
          if (typeof ts.toDate === 'function') {
            d = ts.toDate();
          } else if (typeof ts === 'object' && typeof ts.seconds === 'number') {
            d = new Date(ts.seconds * 1000);
          } else if (ts instanceof Date) {
            d = ts;
          } else if (typeof ts === 'string' || typeof ts === 'number') {
            d = new Date(ts);
          } else {
            return '';
          }
        } catch (e) {
          return '';
        }
        if (!d || isNaN(d.getTime())) return '';
        return d.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        });
      },
      hideUpdateModal() {
        this.showUpdateModal = false;
      },
      show_CancelAccountModal() {
        this.showCancelAccountModal = true;
      },
      hideCancelAccountModal() {
        this.showCancelAccountModal = false;
      },
      hideUnsavedModal() {
        this.showUnsavedModal = false;
        this.allowLeave = true;
        if (this.pendingNext) {
          this.pendingNext();
          this.pendingNext = null;
        }
      },
      saveAndNavigate() {
        this.showUnsavedModal = false;
        this.allowLeave = true;
        this.saveChangesInfo();
        if (this.pendingNext) {
          this.pendingNext();
          this.pendingNext = null;
        }
      },
      hasUnsavedChanges() {
        const strip = (val) => (val || '').replaceAll(' ', '').replaceAll('-', '');
        const el = (id) => {
          const node = window.document.getElementById(id);
          return node ? node.value : null;
        };
        const currentCompany = el("company_name_billing") || this.company_name;
        const currentEmail = el("email_billing") || this.email;
        const currentPhone = strip(el("phone_number_billing") || this.phone_number);
        const currentAddress = this.message_address || '';
        return currentCompany !== this.originalValues.company_name ||
            currentAddress !== this.originalValues.message_address ||
            currentPhone !== strip(this.originalValues.phone_number) ||
            currentEmail !== this.originalValues.email;
      },
      saveChangesInfo() {
        let company_name = window.document.getElementById("company_name_billing").value;
        let company_name_current = '';
        for (const tenant of this.user.tenants_list) {
          if (this.user.tenant_current_logged == tenant.tenant_id) {
            company_name_current = tenant.company_name
          }
        }
        let address = this.message_address || '';
        // Block the save if the user edited the address to something that
        // didn't come from the Places dropdown. A freeform or autofilled
        // value would leave us without coordinates and silently break
        // every future signup-match in this tenant's area.
        if (address !== this.originalValues.message_address && !this.location) {
          this.addressError = 'Please pick your address from the dropdown suggestions.';
          return;
        }
        this.addressError = '';
        let new_phone_number = window.document.getElementById("phone_number_billing").value;
        let email = window.document.getElementById("email_billing").value;
        let phone_raw = new_phone_number;
        new_phone_number = new_phone_number.replaceAll(' ', '').replaceAll('-', '');
        this.originalValues = {
          company_name: company_name,
          message_address: address,
          phone_number: phone_raw,
          email: email,
        };
        this.$store.dispatch('Billing/update_info_company',
          {
            company_name: company_name,
            company_name_current: company_name_current,
            address: address,
            location: this.location,
            new_phone_number: new_phone_number,
            email: email,
            owner_id: this.user.owner_id,
            tenant_id: this.user.tenant_current_logged,
            user: this.user
          }
        ).then(() => {
          this.showUpdateModal = true;
        }).catch((error) => {
          console.error('Save failed:', error);
          this.$store.commit('Billing/success');
        });
      },
      async onExportClick() {
        if (this.exporting || this.exportRequested) return;
        this.exporting = true;
        this.exportError = '';
        try {
          await requestDataExport();
          this.exportRequested = true;
        } catch (err) {
          console.error('Data export request failed:', err);
          this.exportError = "We couldn't start your export. Please try again or contact support@kiey.com.";
        } finally {
          this.exporting = false;
        }
      },
      async loadBillingInfo() {
        this.billing_info_loading = true;
        this.billing_info_error = '';
        try {
          const result = await this.$store.dispatch('Market_billing/fetch_billing_info');
          if (result && result.error) {
            this.billing_info_error = result.message ||
              "We couldn't load your billing details. Please try again.";
          }
        } catch (err) {
          console.error('[billing/list] loadBillingInfo failed:', err);
          this.billing_info_error =
            "We couldn't load your billing details. Please try again.";
        } finally {
          this.billing_info_loading = false;
        }
      },
      // ---- Reactivate flow (cancelled-state card) ------------------
      // Tear down the Stripe Element + related refs before unmounting
      // the form. Without this, the old payment iframe + Stripe event
      // handlers leak when the user clicks Cancel and then re-opens
      // Reactivate, eventually breaking confirmSetup with a stale
      // Elements instance.
      closeReactivate() {
        if (this.reactivate_payment_element && typeof this.reactivate_payment_element.unmount === 'function') {
          try { this.reactivate_payment_element.unmount(); } catch (_) { /* noop */ }
          try { this.reactivate_payment_element.destroy(); } catch (_) { /* noop */ }
        }
        this.reactivate_payment_element = null;
        this.reactivate_elements = null;
        this.reactivate_stripe = null;
        this.reactivate_setup_intent_id = null;
        this.reactivate_elements_ready = false;
        this.reactivate_submitting = false;
        this.reactivate_error = '';
        this.reactivate_load_error = '';
        this.reactivate_open = false;
      },
      async onOpenReactivate() {
        this.reactivate_open = true;
        this.reactivate_error = '';
        this.reactivate_load_error = '';
        this.reactivate_unavailable_zips = [];
        try {
          const intentResp = await axios.post(config.api_url + 'signup_owner/setup_intent');
          const data = intentResp.data && intentResp.data.data;
          if (!data || !data.client_secret) throw new Error('SetupIntent unavailable.');
          this.reactivate_setup_intent_id = data.setup_intent_id;
          const { loadStripe } = await import(/* webpackChunkName: "stripe-js" */ '@stripe/stripe-js');
          this.reactivate_stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
          if (!this.reactivate_stripe) throw new Error('Stripe.js failed to load.');
          this.reactivate_elements = this.reactivate_stripe.elements({
            clientSecret: data.client_secret,
            appearance: { theme: 'stripe', variables: { colorPrimary: '#2bace2', borderRadius: '8px' } },
          });
          this.reactivate_payment_element = this.reactivate_elements.create('payment', {
            fields: { billingDetails: { address: { country: 'auto', line1: 'auto', line2: 'auto', city: 'auto', state: 'auto', postalCode: 'auto' } } },
            defaultValues: { billingDetails: { address: { country: 'US' } } },
            wallets: { link: 'never', applePay: 'never', googlePay: 'never' },
          });
          await this.$nextTick();
          const slot = this.$refs.reactivate_payment_element;
          if (!slot) throw new Error('Payment slot missing.');
          this.reactivate_payment_element.mount(slot);
          this.reactivate_payment_element.on('ready', () => { this.reactivate_elements_ready = true; });
          this.reactivate_payment_element.on('loaderror', (e) => {
            this.reactivate_load_error = (e && e.error && e.error.message) || 'Failed to load the secure payment form.';
          });
        } catch (err) {
          console.error('[reactivate] init failed:', err);
          this.reactivate_load_error = (err && err.message) || 'Failed to load the secure payment form.';
        }
      },

      async onSubmitReactivate() {
        if (this.reactivate_submitting || !this.reactivate_elements_ready) return;
        this.reactivate_submitting = true;
        this.reactivate_error = '';
        try {
          const confirmResult = await this.reactivate_stripe.confirmSetup({
            elements: this.reactivate_elements,
            confirmParams: { return_url: window.location.origin + '/owner/billing' },
            redirect: 'if_required',
          });
          if (confirmResult.error) {
            this.reactivate_error = confirmResult.error.message || 'Could not authorize your card. Please check the details and try again.';
            this.reactivate_submitting = false;
            return;
          }
          const seti = confirmResult.setupIntent;
          if (!seti || seti.status !== 'succeeded') {
            this.reactivate_error = 'Card authorization did not complete. Please try again.';
            this.reactivate_submitting = false;
            return;
          }
          const token = localStorage.getItem('auth_token');
          const resp = await axios.post(
            config.api_url + 'markets/reactivate',
            { setup_intent_id: seti.id },
            { headers: { Authorization: token } },
          );
          const data = resp.data && resp.data.data;
          // Preserve the unavailable-zips list on the component before
          // closeReactivate() resets state — the persistent banner reads
          // it after the inline form unmounts.
          const unavailable = (data && data.zips_unavailable) || [];
          this.closeReactivate();
          this.reactivate_unavailable_zips = unavailable;
          // Refresh billing_info so the page renders the new active card.
          await this.$store.dispatch('Market_billing/fetch_billing_info');
        } catch (err) {
          console.error('[reactivate] submit failed:', err);
          this.reactivate_error = (err && err.response && err.response.data && err.response.data.message)
            || (err && err.message)
            || 'Failed to reactivate. Please try again or contact support.';
        } finally {
          this.reactivate_submitting = false;
        }
      },

      async resumeSubscription() {
        if (this.resuming) return;
        this.resuming = true;
        this.billing_info_error = '';
        try {
          const result = await this.$store.dispatch('Market_billing/resume');
          if (result && result.success) {
            await this.$store.dispatch('Market_billing/fetch_billing_info');
          } else {
            this.billing_info_error = (result && result.message) ||
              "We couldn't resume your subscription. Please try again or contact support.";
          }
        } catch (err) {
          console.error('[billing/list] resumeSubscription failed:', err);
          this.billing_info_error =
            "We couldn't resume your subscription. Please try again or contact support.";
        } finally {
          this.resuming = false;
        }
      },
      async openBillingPortal() {
        if (this.opening_portal) return;
        this.opening_portal = true;
        try {
          const result = await this.$store.dispatch('Market_billing/billing_portal');
          if (result && result.error) {
            this.billing_info_error = result.message ||
              "We couldn't open the billing portal. Please try again.";
            this.opening_portal = false;
          }
          // On success the store redirects via window.location, so this
          // page is unmounting — leave opening_portal=true.
        } catch (err) {
          console.error('[billing/list] openBillingPortal failed:', err);
          this.billing_info_error =
            "We couldn't open the billing portal. Please try again.";
          this.opening_portal = false;
        }
      },
      format_timestamp(ts) {
        if (!ts) return '—';
        // billing_info now sends ms (created * 1000, current_period_end * 1000,
        // status_transitions.paid_at * 1000). Keep seconds → ms fallback for
        // any legacy caller that still passes Unix seconds.
        const ms = typeof ts === 'number'
          ? (ts > 1e12 ? ts : ts * 1000)
          : Date.parse(ts);
        if (!ms || isNaN(ms)) return '—';
        return new Date(ms).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        });
      },
      format_invoice_date(ts) {
        return this.format_timestamp(ts);
      },
      format_invoice_amount(cents) {
        if (typeof cents !== 'number') return '—';
        return (cents / 100).toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      },
      format_invoice_status(status) {
        if (!status) return '—';
        return String(status).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      },
      invoice_status_class(status) {
        if (status === 'paid') return 'invoice-status-paid';
        if (status === 'open') return 'invoice-status-open';
        if (status === 'void' || status === 'uncollectible') return 'invoice-status-void';
        return 'invoice-status-neutral';
      },
      async initAutocomplete() {
        const self = this;
        await this.$gmapApiPromiseLazy();
        await this.$nextTick();
        const input = self.$refs.addressField.$el.querySelector('input');
        if (!input) return;
        const autocomplete = new window.google.maps.places.Autocomplete(input, {
          types: ['address'],
          componentRestrictions: { country: ['us', 'ca'] },
        });
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (place && place.formatted_address) {
            self.message_address = place.formatted_address;
            self.location = extractLocation(place);
          }
        });
      },
    }
  };
</script>

<style lang="scss">
  .custom-container {
    max-width: 1222px !important;
    min-height: auto;
    top: 40px;
    margin-bottom: 50px !important;
    padding: 30px 50px;
  }

  .sub_link_header {
    color: #717e91 !important;
  }

  .drop_custom {
    position: relative;
    left: -16px;
    top: 19px;

    .v-input__slot:before {
    }

    .v-input__slot {
      box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0.2),
      0px 0px 0px 0px rgba(0, 0, 0, 0.14), 0px 0px 0px 0px rgba(0, 0, 0, 0.12) !important;
      border-color: transparent !important;
    }
  }

  .btn-billing {
    font-size: 13px !important;
    font-weight: 400 !important;
    width: 148px !important;
    height: 32px !important;
    letter-spacing: normal;
    text-transform: capitalize;
    position: relative;
    /*box-shadow: 0 5px 18px rgba(51, 51, 51, 0.13)!important;*/
    border-radius: 24px;
    border: 2px solid #29abe2;

    &.btn-blanco--a {
      span {
        color: #2bace2 !important;
      }
    }
  }

  .btn-billing2 {
    font-size: 15px !important;
    font-weight: 700 !important;
    width: 155px !important;
    height: 48px !important;
    letter-spacing: 0.94px;
    text-transform: capitalize;
    position: relative;
    /*box-shadow: 0 5px 18px rgba(51, 51, 51, 0.13)!important;*/
    &.btn-blanco--a {
      span {
        color: #2bace2 !important;
      }
    }
  }

  .billing-title {
    color: #041332;
    font-size: 30px;
    font-weight: 500;
    line-height: 34px;
    letter-spacing: normal;
    padding-bottom: 7px;
  }

  .table_btn {
    color: #2bace2;
    font-size: 13px;
    font-weight: 400;
    text-transform: capitalize;
  }

  .table_botonera {
    /*border-bottom: 1px solid #e8ebef;*/
  }

  .fill-width .mar_table {
    margin: 0 5px;
  }

  .table-f {
    font-size: 13px !important;
  }

  .span_content {
    color: #3256a4;
    font-size: 20px;
    font-weight: 400;
    top: 3px;
    position: relative;
  }

  .span_title {
    color: #061d47;
    font-size: 16px;
    font-weight: 700;
    line-height: 14px;
  }

  .account-header {
    margin: 0 80px;
    padding: 0;
  }

  .account-cancel-notice {
    margin: 8px 80px 16px;
    padding: 12px 14px;
    background: #fde6e6;
    border: 1px solid #e8a4a4;
    border-radius: 8px;
    color: #8a1f1f;
    font-size: 13px;
    line-height: 1.5;
  }
  .account-cancel-notice strong { display: block; margin-bottom: 2px; }
  .account-cancel-notice a { color: inherit; text-decoration: underline; }

  .account-fields {
    margin: 12px 68px;

    .v-text-field .v-label {
      top: 2px;
    }

    .v-text-field input {
      margin-top: 8px;
      padding-bottom: 6px;
    }
  }

  @media (max-width: 768px) {
    .account-header {
      margin: 0 16px !important;
      flex-wrap: wrap;

      .col {
        flex: 0 0 100% !important;
        max-width: 100% !important;

        &:last-child {
          text-align: start !important;
          margin-top: 8px;
        }
      }
    }

    .account-fields {
      margin: 12px 8px !important;

      .col-6 {
        flex: 0 0 100% !important;
        max-width: 100% !important;
      }
    }

    .custom-container {
      padding: 20px 16px !important;
    }

    .content_billing {
      margin: 20px 16px !important;
    }
  }

  @media (max-width: 600px) {
    .account-header {
      margin: 0 8px !important;
    }

    .account-fields {
      margin: 8px 4px !important;
    }

    .custom-container {
      padding: 16px 10px !important;
      top: 10px !important;
    }

    .billing-title {
      font-size: 22px !important;
      line-height: 28px !important;
    }

    .btn-billing {
      width: auto !important;
      font-size: 12px !important;
    }
  }

  @media (max-width: 480px) {
    .custom-container {
      padding: 12px 6px !important;
    }

    .text-center .btn-azul {
      width: 100% !important;
      max-width: 280px;
    }
  }



  .content_billing {
    max-width: 1093px;
    margin: 35px 80px;

    div {
      max-width: 230px;
      width: 25%;
    }

    div:nth-child(1) {
      margin-right: 22px;
    }

    div:nth-child(2) {
    }

    div:nth-child(3) {
    }

  }

  .v-menu__content {
    border-radius: 4px !important;

    .v-list {
      padding: 0px;

      .v-list-item {
        .v-list-item__content {
          padding: 2px 0px;

          .v-list-item__title {
            font-size: 14px;
            text-transform: none;
            color: #717e91;
          }
        }
      }
    }

  }

  .privacy-section {
    margin: 8px 80px 30px;
    padding: 24px 0 8px;
    border-top: 1px solid #e8ebef;
  }
  .privacy-row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 24px;
  }
  .privacy-text { flex: 1; min-width: 0; }
  .privacy-title {
    color: #041332;
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 6px;
  }
  .privacy-desc {
    color: #5e6b85;
    font-size: 13px;
    line-height: 19px;
    margin: 0;
    max-width: 540px;
  }
  .privacy-btn {
    flex: 0 0 auto;
    background: #ffffff;
    border: 1px solid #061d47;
    color: #061d47;
    font-size: 13px;
    font-weight: 500;
    padding: 8px 18px;
    border-radius: 24px;
    cursor: pointer;
    line-height: 1;
    transition: background-color 0.15s ease, color 0.15s ease;
  }
  .privacy-btn:hover:not(:disabled) {
    background: #061d47;
    color: #ffffff;
  }
  .privacy-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .privacy-error {
    color: #c0392b;
    font-size: 13px;
    margin: 12px 0 0;
  }
  .privacy-success {
    color: #2e7d32;
    font-size: 13px;
    margin: 12px 0 0;
  }
  @media (max-width: 768px) {
    .privacy-section { margin: 8px 16px 24px; }
    .privacy-row { flex-direction: column; }
    .privacy-btn { width: 100%; }
  }

  .legal-section {
    margin: 0 80px 30px;
    padding: 16px 0 0;
    border-top: 1px solid #e8ebef;
  }
  .legal-title {
    color: #041332;
    font-size: 18px;
    font-weight: 500;
    margin: 0 0 10px;
  }
  .legal-record {
    color: #2d2d2d;
    font-size: 13px;
    line-height: 19px;
    margin: 0 0 8px;
  }
  .legal-record--unknown {
    color: #6a6a6a;
    font-style: italic;
  }
  .legal-version {
    background: #eef0f5;
    border-radius: 4px;
    padding: 1px 6px;
    font-family: monospace;
    font-size: 12px;
  }
  .legal-links {
    font-size: 13px;
    margin: 0;
  }
  .legal-links a {
    color: #29abe2;
    text-decoration: none;
  }
  .legal-links a:hover {
    text-decoration: underline;
  }
  @media (max-width: 768px) {
    .legal-section { margin: 0 16px 24px; }
  }

  /* ──────────────────────────────────────────────────────────────────
     Billing section — calculated-mode tenants only.
     Plain HTML + scoped CSS (no Vuetify) per project convention.
     ────────────────────────────────────────────────────────────────── */
  .billing-section {
    background: #ffffff;
    border-radius: 12px;
    box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06);
    padding: 32px 50px;
    margin: 0 0 50px;
    box-sizing: border-box;
  }
  .billing-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 18px;
  }
  .billing-section-title {
    color: #041332;
    font-size: 24px;
    font-weight: 500;
    margin: 0;
  }
  .billing-section-action { margin: 0; flex-shrink: 0; }
  .billing-section-subtitle {
    color: #6a6a6a;
    font-size: 13px;
    margin: 0 0 18px;
  }
  .billing-loading {
    color: #6a6a6a;
    font-size: 14px;
    padding: 24px 0;
  }
  .billing-error {
    color: #c0392b;
    font-size: 14px;
    padding: 16px 18px;
    background: #fdecec;
    border-radius: 8px;
  }
  .plan-card {
    border: 1px solid #e4e8f1;
    border-radius: 10px;
    padding: 22px 24px;
    margin-bottom: 28px;
  }
  .free-tier-card {
    background: #f4faff;
    border-color: #cfe4ff;
  }
  .free-tier-blurb {
    margin: 18px 0 0;
    color: #3a4a6b;
    font-size: 13px;
    line-height: 1.5;
  }
  .free-tier-blurb a { color: #2bace2; text-decoration: none; }
  .free-tier-blurb a:hover { text-decoration: underline; }
  .plan-card-row {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
  }
  .plan-card-cell {
    flex: 1 1 160px;
    min-width: 140px;
  }
  .plan-card-label {
    color: #6a7388;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    margin-bottom: 6px;
  }
  .plan-card-value {
    color: #061d47;
    font-size: 16px;
    font-weight: 500;
  }
  .plan-card-actions {
    margin-top: 22px;
    display: flex;
    justify-content: flex-end;
  }
  .manage-billing-btn {
    background: #29abe2;
    color: #ffffff;
    border: none;
    border-radius: 24px;
    padding: 10px 22px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    line-height: 1;
  }
  .manage-billing-btn:hover:not(:disabled) {
    background: #1f9bd0;
  }
  .manage-billing-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.4;
  }
  .status-badge-active { background: #e6f7f4; color: #19a99d; }
  .status-badge-warning { background: #fff4e0; color: #b45309; }
  .status-badge-canceled { background: #fdecec; color: #c0392b; }
  .status-badge-neutral { background: #eef0f5; color: #5e6b85; }

  .invoices-title {
    color: #041332;
    font-size: 16px;
    font-weight: 600;
    margin: 0 0 12px 0;
  }
  .invoices-empty {
    color: #6a6a6a;
    font-size: 13px;
    padding: 12px 0;
  }
  .invoices-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
  }
  .invoices-table thead th {
    text-align: left;
    color: #6a7388;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    padding: 10px 12px;
    border-bottom: 1px solid #e4e8f1;
  }
  .invoices-table tbody td {
    padding: 12px;
    color: #061d47;
    border-bottom: 1px solid #f0f2f6;
  }
  .invoice-status {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
  }
  .invoice-status-paid { background: #e6f7f4; color: #19a99d; }
  .invoice-status-open { background: #fff4e0; color: #b45309; }
  .invoice-status-void { background: #eef0f5; color: #6a7388; }
  .invoice-status-neutral { background: #eef0f5; color: #5e6b85; }
  .invoice-link {
    color: #2bace2;
    font-weight: 500;
    text-decoration: none;
  }
  .invoice-link:hover { text-decoration: underline; }
  .invoice-link-download { margin-left: 40px; }
  /* ---- Cancelled-state card + Reactivate flow ---- */
  .cancelled-card {
    background: #fff7e0;
    border: 1px solid #f0c97d;
    border-radius: 12px;
    padding: 24px 28px;
    margin-top: 8px;
  }
  .cancelled-history-card {
    margin-top: 24px;
  }
  .cancelled-card-row {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    flex-wrap: wrap;
  }
  .cancelled-card-icon {
    font-size: 28px;
    line-height: 1;
    color: #92580b;
    flex-shrink: 0;
  }
  .cancelled-card-body { flex: 1; min-width: 0; }
  .cancelled-card-title {
    font-size: 18px;
    color: #041332;
    margin: 0 0 4px;
    font-weight: 600;
  }
  .cancelled-card-meta { color: #6b7280; font-size: 13px; margin: 0 0 8px; }
  .cancelled-card-detail { color: #041332; font-size: 13px; margin-bottom: 8px; }
  .cancelled-card-help { color: #5e6b85; font-size: 13px; margin: 0; }
  .reactivate-btn {
    background: #061D47;
    color: #fff;
    border: none;
    padding: 10px 22px;
    border-radius: 999px;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;
    align-self: center;
  }
  .reactivate-btn:hover:not(:disabled) { background: #0a2e6f; }
  .reactivate-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .reactivate-flow {
    margin-top: 22px;
    padding-top: 22px;
    border-top: 1px solid #f0c97d;
  }
  .reactivate-element-mount { min-height: 60px; }
  .reactivate-loading, .reactivate-error {
    color: #92580b;
    font-size: 13px;
    margin: 8px 0;
  }
  .reactivate-error { color: #c0392b; }
  .reactivate-warning {
    background: #fff;
    border: 1px solid #f0c97d;
    border-radius: 8px;
    padding: 10px 12px;
    margin-top: 12px;
    color: #92580b;
    font-size: 13px;
  }
  .reactivate-warning--persistent {
    margin: 0 0 16px;
    background: #fff7e8;
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }
  .reactivate-warning-dismiss {
    background: transparent;
    border: 1px solid #d8a350;
    color: #92580b;
    border-radius: 6px;
    padding: 4px 10px;
    cursor: pointer;
    font-size: 12px;
    flex-shrink: 0;
  }
  .reactivate-price-line {
    margin: 14px 0 6px;
    color: #1f3357;
    font-size: 13px;
    line-height: 1.5;
  }
  .reactivate-price-line strong { color: #061d47; }
  .reactivate-actions {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
  }
  .reactivate-cancel {
    background: none;
    border: 1px solid #d4dbe8;
    color: #5e6b85;
    padding: 8px 18px;
    border-radius: 999px;
    cursor: pointer;
    font-weight: 500;
  }
  .reactivate-cancel:hover:not(:disabled) { background: #f5f8fc; }
  .reactivate-submit {
    background: #2bace2;
    color: #fff;
    border: none;
    padding: 8px 22px;
    border-radius: 999px;
    cursor: pointer;
    font-weight: 600;
  }
  .reactivate-submit:hover:not(:disabled) { background: #1e8bb3; }
  .reactivate-submit:disabled, .reactivate-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

  .cancel-link-row {
    display: flex;
    justify-content: flex-end;
    margin-top: 24px;
  }
  .cancel-subscription-link {
    background: none;
    border: none;
    color: #c0392b;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    padding: 6px 8px;
    text-decoration: underline;
  }
  .cancel-subscription-link:hover { color: #a4321f; }
  .resume-subscription-link {
    background: none;
    border: none;
    color: #2bace2;
    font-weight: 600;
    cursor: pointer;
    font-size: 14px;
    text-decoration: underline;
  }
  .resume-subscription-link:hover { color: #1e8bb3; }
  .resume-subscription-link:disabled { opacity: 0.5; cursor: not-allowed; text-decoration: none; }
  .plan-card-subnote {
    color: #6a7388;
    font-size: 11px;
    margin-top: 4px;
  }

  @media (max-width: 768px) {
    .billing-section { margin: 0 16px 30px; padding: 24px 18px; }
    .plan-card { padding: 16px 14px; }
    .plan-card-row { gap: 16px; }
    .plan-card-cell { flex: 1 1 45%; }
    .invoices-table { font-size: 12px; }
    .invoices-table thead th, .invoices-table tbody td { padding: 8px 6px; }
  }
</style>
